import { useState } from "react";
import { Copy, Check, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";

const GRADE_COLOR = { A: "#4CAF6A", B: "#7CB342", C: "#F59E0B", D: "#F97316", E: "#EF4444" };
export const gradeOf = (s) => (s >= 90 ? "A" : s >= 75 ? "B" : s >= 55 ? "C" : s >= 35 ? "D" : "E");

export function ScoreRing({ score, size = 120, stroke = 10, label }) {
  const grade = gradeOf(score);
  const r = (size - stroke) / 2, c = 2 * Math.PI * r;
  return (
    <div className="flex flex-col items-center" data-testid="score-ring">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={GRADE_COLOR[grade]} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - score / 100)} transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dashoffset 1s cubic-bezier(0.22,1,0.36,1)" }} />
        <text x="50%" y="48%" textAnchor="middle" fontFamily="Playfair Display, serif" fontWeight="700" fontSize={size * 0.26} fill="#0F172A">{score}</text>
        <text x="50%" y="68%" textAnchor="middle" fontFamily="IBM Plex Mono, monospace" fontSize={size * 0.09} fill="#64748B">/100 · {grade}</text>
      </svg>
      {label && <p className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">{label}</p>}
    </div>
  );
}

export function ShareBadge({ scanId, score }) {
  const { t } = useLang();
  const [copied, setCopied] = useState(false);
  const base = process.env.REACT_APP_BACKEND_URL;
  const svgUrl = `${base}/api/badge/${scanId}/svg`;
  const pageUrl = `${window.location.origin}/badge/${scanId}`;
  const embed = `<a href="${pageUrl}"><img src="${svgUrl}" alt="EmpCo score de conformité ${score}/100" width="260" height="56"/></a>`;
  const copy = async () => {
    await navigator.clipboard.writeText(embed);
    setCopied(true); toast.success(t("badge.copied")); setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="hairline p-5" data-testid="share-badge">
      <div className="flex items-center gap-2 mb-3"><Share2 className="w-4 h-4 text-primary" /><p className="eyebrow">{t("badge.title")}</p></div>
      <img src={svgUrl} alt="badge" className="h-14 mb-3" data-testid="badge-preview" />
      <p className="text-xs text-muted-foreground leading-relaxed mb-3">{t("badge.hint")}</p>
      <div className="flex gap-2">
        <code className="flex-1 text-[10px] font-mono bg-muted rounded-md p-2 truncate">{embed}</code>
        <button onClick={copy} className="btn-outline !py-1.5 !px-3 text-xs" data-testid="badge-copy-btn">{copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {t("badge.copy")}</button>
      </div>
      <a href={pageUrl} target="_blank" rel="noreferrer" className="text-xs text-primary link-underline mt-3 inline-block" data-testid="badge-public-link">{t("badge.public")}</a>
    </div>
  );
}
